import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaArrowsAltH, FaPlay, FaPause } from 'react-icons/fa';
import './BeforeAfterVideoSlider.css';

const BeforeAfterVideoSlider = ({ 
  beforeVideo, 
  afterVideo, 
  alt = "Before and after video comparison",
  autoplay = false,
  loop = true,
  muted = true,
  beforePoster,
  afterPoster
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const containerRef = useRef(null);
  const beforeVideoRef = useRef(null);
  const afterVideoRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [beforeSrc, setBeforeSrc] = useState('');
  const [afterSrc, setAfterSrc] = useState('');

  // Lazy-load sources when in view
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        });
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView) {
      setBeforeSrc(beforeVideo);
      setAfterSrc(afterVideo);
    }
  }, [isInView, beforeVideo, afterVideo]);

  // Sync video playback
  useEffect(() => {
    if (beforeVideoRef.current && afterVideoRef.current) {
      if (isPlaying) {
        beforeVideoRef.current.play().catch(e => console.log('Video play error:', e));
        afterVideoRef.current.play().catch(e => console.log('Video play error:', e));
      } else {
        beforeVideoRef.current.pause();
        afterVideoRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Sync video times
  useEffect(() => {
    if (beforeVideoRef.current && afterVideoRef.current && isPlaying) {
      const syncTime = () => {
        if (beforeVideoRef.current && afterVideoRef.current) {
          const currentTime = beforeVideoRef.current.currentTime;
          if (Math.abs(afterVideoRef.current.currentTime - currentTime) > 0.1) {
            afterVideoRef.current.currentTime = currentTime;
          }
        }
      };
      
      const interval = setInterval(syncTime, 100);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e) => {
    setHasInteracted(true);
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setSliderPosition(prev => Math.max(0, prev - 5));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setSliderPosition(prev => Math.min(100, prev + 5));
        break;
      case 'Home':
        e.preventDefault();
        setSliderPosition(0);
        break;
      case 'End':
        e.preventDefault();
        setSliderPosition(100);
        break;
      default:
        break;
    }
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setHasInteracted(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    setHasInteracted(true);
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!containerRef.current || !e.touches[0]) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    });
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Global event listeners for mouse/touch
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Handle video ended
  const handleVideoEnd = useCallback(() => {
    if (loop) {
      if (beforeVideoRef.current) beforeVideoRef.current.currentTime = 0;
      if (afterVideoRef.current) afterVideoRef.current.currentTime = 0;
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [loop]);

  // Memoize clip path calculation
  const clipPath = useMemo(() => `inset(0 ${100 - sliderPosition}% 0 0)`, [sliderPosition]);

  return (
    <div 
      ref={containerRef}
      className={`before-after-video-container ${isDragging ? 'dragging' : ''}`}
      role="region"
      aria-label={alt}
    >
      {/* After Video (Background) */}
      <div className="after-video-container">
        <video 
          ref={afterVideoRef}
          src={afterSrc}
          className="after-video"
          loop={loop}
          muted={muted}
          playsInline
          preload="metadata"
          poster={afterPoster}
          onEnded={handleVideoEnd}
        />
        <div 
          className="video-label after-label"
          style={{ opacity: sliderPosition > 85 ? 0 : 1 }}
        >
          After
        </div>
      </div>
      
      {/* Before Video (Clipped) */}
      <div 
        className="before-video-container"
        style={{ clipPath }}
      >
        <video 
          ref={beforeVideoRef}
          src={beforeSrc}
          className="before-video"
          loop={loop}
          muted={muted}
          playsInline
          preload="metadata"
          poster={beforePoster}
          onEnded={handleVideoEnd}
        />
        <div 
          className="video-label before-label"
          style={{ opacity: sliderPosition < 15 ? 0 : 1 }}
        >
          Before
        </div>
      </div>
      
      {/* Slider Handle */}
      <div 
        className="video-slider-handle"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onKeyDown={handleKeyDown}
        role="slider"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={sliderPosition}
        aria-label="Drag to compare before and after videos. Use left and right arrow keys to adjust."
        tabIndex={0}
      >
        <div className="video-slider-line"></div>
        <div className="video-slider-icon">
          <FaArrowsAltH />
        </div>
        <div className="video-slider-line"></div>
      </div>
      
      {/* Play/Pause Button */}
      <button 
        className="video-play-pause-btn"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      
      {/* Instructions (hidden permanently after first interaction) */}
      {!hasInteracted && (
        <div className="video-slider-instructions">
          <span className="instruction-text">Drag to compare • Click to play/pause • Use arrow keys to adjust</span>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterVideoSlider;

